import { useId } from "react";

/**
 * Small charts, drawn as inline SVG.
 *
 * No charting library: the whole set here is a few hundred lines, and the
 * alternative is ~100kB of bundle for four shapes. Everything scales with its
 * container via `viewBox` + `preserveAspectRatio`, so these behave on a phone
 * without a resize observer.
 *
 * Colours come from the Tailwind palette as literals rather than classes,
 * because SVG geometry attributes (`stroke`, `fill`) can't read a CSS class.
 */

export const CHART_COLORS = {
  grid: "#25313f",
  axis: "#334254",
  text: "#6f8095",
  win: "#4ade80",
  loss: "#f87171",
  flag: "#fbbf24",
  neutral: "#8d9cb0",
} as const;

/** A distinguishable line per manager. Colour-blind-safe ordering. */
export const SERIES_COLORS = [
  "#4ade80",
  "#60a5fa",
  "#fbbf24",
  "#f87171",
  "#c084fc",
  "#22d3ee",
  "#fb923c",
  "#a3e635",
  "#f472b6",
  "#94a3b8",
];

function niceTicks(min: number, max: number, count = 4): number[] {
  if (max <= min) return [min];
  const raw = (max - min) / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) {
    out.push(Math.round(v * 1000) / 1000);
  }
  return out;
}

// ---------------------------------------------------------------------------

export interface Series {
  label: string;
  color?: string;
  /** one y per x index; null breaks the line (a bye, a game not yet played) */
  points: (number | null)[];
}

/**
 * Multi-series line chart over a shared, evenly spaced x axis.
 *
 * Used for the season race, where x is the week and every manager shares it.
 */
export function LineChart({
  series,
  xLabels,
  height = 220,
  yLabel,
  formatY = (v: number) => String(v),
  highlight,
}: {
  series: Series[];
  xLabels: string[];
  height?: number;
  yLabel?: string;
  formatY?: (v: number) => string;
  /** series label to draw thicker; others fade */
  highlight?: string | null;
}) {
  const id = useId();
  const width = 720;
  const pad = { top: 12, right: 96, bottom: 26, left: 34 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const values = series.flatMap((s) => s.points.filter((p): p is number => p != null));
  if (values.length === 0 || xLabels.length === 0) return null;
  const min = Math.min(0, ...values);
  const max = Math.max(...values);
  const ticks = niceTicks(min, max);
  const top = Math.max(max, ticks[ticks.length - 1] ?? max);

  const x = (i: number) =>
    pad.left + (xLabels.length === 1 ? plotW / 2 : (i / (xLabels.length - 1)) * plotW);
  const y = (v: number) => pad.top + plotH - ((v - min) / (top - min || 1)) * plotH;

  // Label every week on a short season, every other one when it gets crowded.
  const labelEvery = xLabels.length > 10 ? 2 : 1;

  // End-of-line labels, nudged apart so a five-way tie stays readable. Sorted
  // by position, then each pushed below the previous one when they'd collide;
  // a second pass from the bottom keeps the stack inside the plot.
  const MIN_GAP = 11;
  const endLabels = series
    .map((s, si) => {
      const lastIndex = s.points.reduce<number>((acc, p, i) => (p != null ? i : acc), -1);
      if (lastIndex < 0) return null;
      return {
        label: s.label,
        color: s.color ?? SERIES_COLORS[si % SERIES_COLORS.length],
        x: x(lastIndex),
        y: y(s.points[lastIndex]!),
        labelY: y(s.points[lastIndex]!) + 3,
      };
    })
    .filter((e): e is NonNullable<typeof e> => e != null)
    .sort((a, b) => a.labelY - b.labelY);

  for (let i = 1; i < endLabels.length; i++) {
    const gap = endLabels[i].labelY - endLabels[i - 1].labelY;
    if (gap < MIN_GAP) endLabels[i].labelY = endLabels[i - 1].labelY + MIN_GAP;
  }
  const overflow = endLabels.length
    ? endLabels[endLabels.length - 1].labelY - (height - pad.bottom)
    : 0;
  if (overflow > 0) for (const e of endLabels) e.labelY -= overflow;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full"
      role="img"
      aria-label={yLabel ? `${yLabel} by ${xLabels.length} weeks` : "Line chart"}
    >
      {ticks.map((t) => (
        <g key={t}>
          <line
            x1={pad.left}
            x2={pad.left + plotW}
            y1={y(t)}
            y2={y(t)}
            stroke={CHART_COLORS.grid}
            strokeWidth={1}
          />
          <text
            x={pad.left - 6}
            y={y(t) + 3}
            textAnchor="end"
            fontSize={10}
            fill={CHART_COLORS.text}
          >
            {formatY(t)}
          </text>
        </g>
      ))}

      {xLabels.map((l, i) =>
        i % labelEvery === 0 ? (
          <text
            key={`${l}-${i}`}
            x={x(i)}
            y={height - 8}
            textAnchor="middle"
            fontSize={10}
            fill={CHART_COLORS.text}
          >
            {l}
          </text>
        ) : null,
      )}

      {/*
        Series that finish level land their end labels on the same pixel, which
        in a six-manager league is the normal case, not the edge case. Labels
        are laid out separately from the lines: collect the end points, then
        push overlapping ones apart before drawing any text.
      */}
      {series.map((s, si) => {
        const color = s.color ?? SERIES_COLORS[si % SERIES_COLORS.length];
        const faded = highlight != null && highlight !== s.label;
        // Build one path per unbroken run so a gap stays a gap.
        const runs: string[] = [];
        let current: string[] = [];
        s.points.forEach((p, i) => {
          if (p == null) {
            if (current.length) runs.push(current.join(" "));
            current = [];
            return;
          }
          current.push(`${current.length ? "L" : "M"}${x(i)},${y(p)}`);
        });
        if (current.length) runs.push(current.join(" "));

        // Typed explicitly: inferred from a `(number | null)[]`, the accumulator
        // widens to `number | null` and every use of it below stops compiling.
        const lastIndex = s.points.reduce<number>((acc, p, i) => (p != null ? i : acc), -1);
        return (
          <g key={`${id}-${s.label}`} opacity={faded ? 0.28 : 1}>
            {runs.map((d, i) => (
              <path
                key={i}
                d={d}
                fill="none"
                stroke={color}
                strokeWidth={highlight === s.label ? 2.75 : 1.75}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
            {lastIndex >= 0 && (
              <circle cx={x(lastIndex)} cy={y(s.points[lastIndex]!)} r={2.5} fill={color} />
            )}
          </g>
        );
      })}

      {endLabels.map((e) => (
        <g key={`label-${e.label}`} opacity={highlight != null && highlight !== e.label ? 0.35 : 1}>
          {/* A leader line, since the text may have been nudged off its point. */}
          {Math.abs(e.labelY - e.y) > 1 && (
            <line
              x1={e.x + 3}
              y1={e.y}
              x2={e.x + 7}
              y2={e.labelY - 3}
              stroke={e.color}
              strokeWidth={0.75}
              opacity={0.5}
            />
          )}
          <text x={e.x + 8} y={e.labelY} fontSize={10} fill={e.color}>
            {e.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------

export interface ScatterPoint {
  x: number;
  y: number;
  /** bubble weight, e.g. how many times a team was used */
  weight?: number;
  label: string;
  title?: string;
}

/**
 * Scatter with an optional least-squares trend line.
 *
 * Built for "does a higher rating actually win", where each point is a
 * franchise: rating on x, win rate on y, bubble size for sample.
 */
export function Scatter({
  points,
  height = 260,
  xLabel,
  yLabel,
  formatY = (v: number) => String(v),
  trend = true,
}: {
  points: ScatterPoint[];
  height?: number;
  xLabel?: string;
  yLabel?: string;
  formatY?: (v: number) => string;
  trend?: boolean;
}) {
  if (points.length === 0) return null;
  const width = 720;
  const pad = { top: 12, right: 14, bottom: 34, left: 40 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const xMin = Math.min(...xs) - 1;
  const xMax = Math.max(...xs) + 1;
  const yMin = Math.min(0, ...ys);
  const yMax = Math.max(...ys);
  const yTicks = niceTicks(yMin, yMax, 4);
  const yTop = Math.max(yMax, yTicks[yTicks.length - 1] ?? yMax);
  const xTicks = niceTicks(xMin, xMax, 5);

  const X = (v: number) => pad.left + ((v - xMin) / (xMax - xMin || 1)) * plotW;
  const Y = (v: number) => pad.top + plotH - ((v - yMin) / (yTop - yMin || 1)) * plotH;

  const maxWeight = Math.max(1, ...points.map((p) => p.weight ?? 1));
  const radius = (w: number | undefined) => 3 + 6 * Math.sqrt((w ?? 1) / maxWeight);

  // Least squares, weighted by sample so a one-game franchise doesn't drag the
  // line as hard as an eight-game one.
  let line: { x1: number; y1: number; x2: number; y2: number } | null = null;
  if (trend && points.length >= 3) {
    const w = points.map((p) => p.weight ?? 1);
    const sw = w.reduce((a, b) => a + b, 0);
    const mx = points.reduce((a, p, i) => a + p.x * w[i], 0) / sw;
    const my = points.reduce((a, p, i) => a + p.y * w[i], 0) / sw;
    const num = points.reduce((a, p, i) => a + w[i] * (p.x - mx) * (p.y - my), 0);
    const den = points.reduce((a, p, i) => a + w[i] * (p.x - mx) ** 2, 0);
    if (den > 0) {
      const slope = num / den;
      const at = (x: number) => my + slope * (x - mx);
      line = { x1: X(xMin), y1: Y(at(xMin)), x2: X(xMax), y2: Y(at(xMax)) };
    }
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={`${yLabel ?? "y"} against ${xLabel ?? "x"}`}>
      {yTicks.map((t) => (
        <g key={`y${t}`}>
          <line x1={pad.left} x2={width - pad.right} y1={Y(t)} y2={Y(t)} stroke={CHART_COLORS.grid} />
          <text x={pad.left - 6} y={Y(t) + 3} textAnchor="end" fontSize={10} fill={CHART_COLORS.text}>
            {formatY(t)}
          </text>
        </g>
      ))}
      {xTicks.map((t) => (
        <text key={`x${t}`} x={X(t)} y={height - 16} textAnchor="middle" fontSize={10} fill={CHART_COLORS.text}>
          {t}
        </text>
      ))}

      {line && (
        <line
          {...line}
          stroke={CHART_COLORS.flag}
          strokeWidth={1.5}
          strokeDasharray="5 4"
          opacity={0.8}
        />
      )}

      {points.map((p, i) => (
        <g key={`${p.label}-${i}`}>
          <title>{p.title ?? p.label}</title>
          <circle
            cx={X(p.x)}
            cy={Y(p.y)}
            r={radius(p.weight)}
            fill={CHART_COLORS.win}
            fillOpacity={0.28}
            stroke={CHART_COLORS.win}
            strokeWidth={1}
          />
        </g>
      ))}

      {xLabel && (
        <text x={pad.left + plotW / 2} y={height - 2} textAnchor="middle" fontSize={10} fill={CHART_COLORS.text}>
          {xLabel}
        </text>
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------

/**
 * A labelled horizontal bar. Used inline in tables where a number alone hides
 * how big the gaps are — ban rates, usage shares.
 */
export function BarRow({
  value,
  max,
  label,
  color = CHART_COLORS.win,
}: {
  value: number;
  max: number;
  label?: string;
  color?: string;
}) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <span className="flex items-center gap-2">
      <span className="h-1.5 w-full min-w-[40px] max-w-[120px] overflow-hidden rounded-full bg-field-800">
        <span
          className="block h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </span>
      {label != null && (
        <span className="shrink-0 font-mono text-xs text-chalk-400">{label}</span>
      )}
    </span>
  );
}
